import React, { useState, useEffect, useMemo, useCallback } from "react";

import {
  StyledFeedPage,
  StyledFeedBlock,
  StyledHorizontalCarousel,
} from "./styles";
import {
  BottomNav,
  ErrorScreen,
  Header,
  LoaderComponent,
} from "../../components/";
import assets from "../../assets/";
import { useReservations, useReservationGuidebooks } from "../../hooks/";
import { Reservation } from "../../types/reservation";
import { Guidebook } from "../../types/guidebook";
import { useBookingEngine } from "../../context/BookingEngineContext";

type ReservationsResponse = {
  items: Reservation[];
  pagination: {
    page: number;
    items_per_page: number;
    total_items: number;
  };
};

// Component to fetch and manage guidebooks for a single reservation
const ReservationGuidebooks: React.FC<{
  reservation: Reservation;
  onGuidebooksLoaded: (reservationUid: string, guidebooks: Guidebook[]) => void;
}> = ({ reservation, onGuidebooksLoaded }) => {
  const { guidebooks, loading, error } = useReservationGuidebooks(reservation.uid);

  useEffect(() => {
    if (!loading) {
      // Always call onGuidebooksLoaded when loading is finished, even if there's an error
      // or guidebooks is empty. This prevents infinite loading in the parent component.
      onGuidebooksLoaded(reservation.uid, guidebooks || []);
    }
  }, [guidebooks, loading, error, reservation.uid, onGuidebooksLoaded]);

  return null; // This component doesn't render anything
};

const FeedScreen = () => {
  const { bookingEngineData } = useBookingEngine();
  const { reservations, loading, error } = useReservations();
  const [reservationGuidebooks, setReservationGuidebooks] = useState<
    Record<string, Guidebook[]>
  >({});
  const [guidebooksLoading, setGuidebooksLoading] = useState(true);

  const reservationsList = useMemo(
    () => (reservations as unknown as ReservationsResponse)?.items || [],
    [reservations]
  );

  const getTodayReservations = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return reservationsList.filter((reservation) => {
      const fromDate = new Date(reservation.fromDate);
      fromDate.setHours(0, 0, 0, 0);

      const toDate = new Date(reservation.toDate);
      toDate.setHours(0, 0, 0, 0);

      return fromDate <= today && today <= toDate;
    });
  };

  const getUpcomingReservations = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return reservationsList.filter((reservation) => {
      const fromDate = new Date(reservation.fromDate);
      fromDate.setHours(0, 0, 0, 0);

      const toDate = new Date(reservation.toDate);
      toDate.setHours(0, 0, 0, 0);

      return fromDate > today && toDate > today;
    });
  };

  const todayReservations = getTodayReservations();
  const upcomingReservations = getUpcomingReservations();

  // Track guidebook loading state based on pending reservations
  useEffect(() => {
    if (reservationsList.length === 0) {
      setGuidebooksLoading(false);
      return;
    }

    const hasPendingGuidebooks = reservationsList.some(
      (reservation) => reservationGuidebooks[reservation.uid] === undefined
    );

    setGuidebooksLoading(hasPendingGuidebooks);
  }, [reservationGuidebooks, reservationsList]);

  // Get all unique guidebooks across all reservations
  const getAllGuidebooks = () => {
    const allGuidebooks: Guidebook[] = [];
    const seenUids = new Set<string>();

    reservationsList.forEach((reservation) => {
      const guidebooks = reservationGuidebooks[reservation.uid] || [];
      guidebooks.forEach((guidebook) => {
        if (!seenUids.has(guidebook.uid)) {
          seenUids.add(guidebook.uid);
          allGuidebooks.push(guidebook);
        }
      });
    });

    return allGuidebooks;
  };

  const allGuidebooks = getAllGuidebooks();

  const getGuidebooksForReservation = (reservation: Reservation): Guidebook[] => {
    return reservationGuidebooks[reservation.uid] || [];
  };

  const getMainImageFromAllocatedUnits = (reservation: Reservation) => {
    const allocatedUnit = reservation.allocatedUnits?.[0];

    if (allocatedUnit?.mainImage?.image) {
      var imageUrl = allocatedUnit.mainImage.image.urls?.big;
      if (typeof imageUrl === "string") {
        imageUrl = imageUrl.replace("https://", "http://");
      }
      return imageUrl;
    } else {
      return "";
    }
  };

  const handleGuidebooksLoaded = useCallback(
    (reservationUid: string, guidebooks: Guidebook[]) => {
      setReservationGuidebooks((prev) => {
        if (prev[reservationUid] === guidebooks) return prev;

        return {
          ...prev,
          [reservationUid]: guidebooks,
        };
      });
    },
    []
  );

  // Render hidden components to fetch guidebooks for each reservation
  const renderGuidebookFetchers = () => {
    return reservationsList.map((reservation) => (
      <ReservationGuidebooks
        key={reservation.uid}
        reservation={reservation}
        onGuidebooksLoaded={handleGuidebooksLoaded}
      />
    ));
  };

  // Handle loading state
  if (loading) {
    return <LoaderComponent content="Loading reservations..." />;
  }

  // Handle error state
  if (error) {
    return (
      <ErrorScreen
        content={"Please try again, later."}
        imageSrc={assets.NoResultsVector}
      />
    );
  }

  // Handle no reservations
  if (reservationsList.length === 0) {
    return (
      <StyledFeedPage>
        <Header />
        <h1 style={{ marginTop: -20 }}>
          Your
          <br /> Guidebooks
        </h1>
        <img
          height={400}
          src={assets.DissapointmentVector}
          alt="No Guidebooks in the feed."
          style={{
            filter: bookingEngineData ? "grayscale(1)" : "grayscale(0)",
          }}
        />
        <p style={{ width: "70%", textAlign: "center" }}>
          No Guidebooks were added, yet.
        </p>
        <BottomNav />
      </StyledFeedPage>
    );
  }

  // Always render guidebook fetchers to trigger API calls
  const guidebookFetchers = renderGuidebookFetchers();

  // Handle guidebooks loading
  if (guidebooksLoading) {
    return (
      <>
        {guidebookFetchers}
        <LoaderComponent content="Loading guidebooks..." />
      </>
    );
  }

  // Handle no guidebooks
  if (allGuidebooks.length === 0) {
    return (
      <>
        {guidebookFetchers}
        <StyledFeedPage>
          <Header />
          <h1 style={{ marginTop: -20 }}>
            Your
            <br /> Guidebooks
          </h1>
          <img
            height={400}
            src={assets.DissapointmentVector}
            alt="No Guidebooks in the feed."
            style={{
              filter: bookingEngineData ? "grayscale(1)" : "grayscale(0)",
            }}
          />
          <p style={{ width: "70%", textAlign: "center" }}>
            No Guidebooks were added, yet.
          </p>
          <BottomNav />
        </StyledFeedPage>
      </>
    );
  }

  // Render guidebooks
  return (
    <>
      {guidebookFetchers}
      <StyledFeedPage>
        <Header />
        <h1>
          Your <br /> Guidebooks
        </h1>
        <StyledHorizontalCarousel>
          {allGuidebooks.map((guidebook: Guidebook) => {
            // Prefer current/upcoming stays, but keep every displayed guidebook accessible.
            const reservationWithGuidebook = [...todayReservations, ...upcomingReservations, ...reservationsList].find(
              (res) => res.allocatedUnits?.[0]?.uid &&
                getGuidebooksForReservation(res).some((gb) => gb.uid === guidebook.uid)
            );
            const unitUid = reservationWithGuidebook?.allocatedUnits?.[0]?.uid;

            return (
              <StyledFeedBlock
                key={guidebook.uid}
                to={unitUid
                  ? `/guidebooks/${guidebook.uid}/units/${unitUid}`
                  : `/guidebooks/${guidebook.uid}`}
              >
                <h4>{guidebook.title}</h4>
                {reservationWithGuidebook && (
                  <img
                    src={getMainImageFromAllocatedUnits(reservationWithGuidebook)}
                    alt={guidebook.title}
                  />
                )}
              </StyledFeedBlock>
            );
          })}
        </StyledHorizontalCarousel>

        <BottomNav />
      </StyledFeedPage>
    </>
  );
};

export default FeedScreen;
